import {
  VaultDefaultThumbnailIcon,
  AlertTriangleIcon,
  ChevronIcon,
  ClockIcon,
  LockIcon,
  MinusCircleIcon,
  NewWindowIcon,
} from 'assets/icons';
import { Badge } from 'common/badge';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { ThreeStatesCheckbox } from 'common/checkbox-three-states';
import {
  Breakpoints,
  InvitationStatusId,
  NotificationType,
} from 'common/enums';
import { appActions, AppContext } from 'context';
import {
  collaboratorsActions,
  CollaboratorsContext,
} from 'context/collaborators-context';
import {
  capitalizeFirstLetter,
  classnames,
  isLockedVault,
  isOwner,
  isRestrictedVault,
} from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, { useContext, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { goToPage, RouteName } from 'routes';
import styles from './vault-row.module.scss';

type VaultRowProps = {
  vault: VaultCollaboratorsType;
  user: UserType;
  isOpened: boolean;
  isSelected?: boolean | null;
  toggleIsOpened: () => void;
  handleSelectVault: () => void;
};

const VaultRow: React.FC<VaultRowProps> = ({
  vault,
  user,
  isOpened,
  isSelected,
  toggleIsOpened,
  handleSelectVault,
}) => {
  const {
    state: { isLoading },
    dispatch: collaboratorsDispatch,
  } = useContext(CollaboratorsContext);
  const { dispatch: generalDispatch } = useContext(AppContext);
  const [showDefaultImage, setShowDefaultImage] = useState(!vault.coverUrl);
  const [invitesSent, setInvitesSent] = useState(false);

  const isLocked = isLockedVault(vault);
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.xs}px)`);

  const totalInvites = vault.collaborators.length;
  const pendingInvites = vault.collaborators.filter(
    (collaborator) =>
      collaborator.invitationStatus === InvitationStatusId.pending,
  );

  const handleResendInvites = async (
    collaborators: VaultCollaboratorType[],
  ) => {
    if (invitesSent) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: `${pendingInvites.length} pending invites sent by you have already been resent. Try again later.`,
          noMessage: true,
          type: NotificationType.Error,
          icon: <AlertTriangleIcon />,
        }),
      });
      return;
    }

    collaborators.forEach(async (collaborator) => {
      try {
        if (collaborator.type === 'contributor') {
          await VaultsController.resendVaultInvitation(
            collaborator.invitationId!,
          );
        } else {
          await VaultsController.inviteGuestsToRecord({
            vaultId: vault.id,
            guests: [{ name: collaborator.name, email: collaborator.email }],
          });
        }

        collaboratorsDispatch({
          type: collaboratorsActions.UPDATE_COLLABORATOR,
          editCollaboratorInfo: {
            vaultId: vault.id,
            collaborator: {
              ...collaborator,
              invitationStatus: InvitationStatusId.pending,
            },
          },
        });
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: `${pendingInvites.length} invites resent.`,
            type: NotificationType.Info,
            noMessage: true,
          }),
        });
      } catch (err: any) {
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Resend invitation',
            message: `Invitation for ${collaborator.email} couldn't be sent`,
            type: NotificationType.Error,
          }),
        });
      }
    });
    setInvitesSent(true);
  };

  const showPendingInvitations = () => (
    <div>
      {pendingInvites.length > 0 && !isRestrictedVault(vault) && !isLocked && (
        <div className={styles.pendingContainer}>
          <div className={styles.content}>
            <ClockIcon />
            <span className="text__body__regular__small__gray700">
              <strong>{pendingInvites.length} </strong>
              {!isMobile && 'out'} of <strong>{totalInvites}</strong> invites
              pending
            </span>
          </div>
          <Button
            buttonSize={ButtonSize.Small}
            buttonStyle={ButtonStyle.PrimaryGhost}
            onClick={(event) => {
              event.stopPropagation();
              handleResendInvites(pendingInvites);
            }}
            disabled={invitesSent}
          >
            <span className="text__body__regular__small__brand500">
              {invitesSent ? 'Invite(s) sent!' : 'Resend all'}
            </span>
          </Button>
        </div>
      )}
    </div>
  );

  if (isLoading)
    return (
      <div className={classnames(styles.vaultRow, styles.vaultPlaceholder)} />
    );
  return (
    <button
      type="button"
      className={classnames(styles.vaultRow)}
      onClick={toggleIsOpened}
    >
      <div className={styles.leftSide}>
        <div className={styles.checkboxAndImage}>
          <div className={styles.checkbox}>
            <ThreeStatesCheckbox
              itemKey={`${vault.id}`}
              checkValue={isSelected}
              onChange={handleSelectVault}
            />
          </div>

          {showDefaultImage ? (
            <VaultDefaultThumbnailIcon
              className={classnames(styles.thumbnail, {
                [styles.grayThumbnail]: isLocked,
              })}
            />
          ) : (
            <img
              onError={(event) => {
                const target = event.currentTarget as HTMLImageElement;
                target.style.display = 'none';
                setShowDefaultImage(true);
              }}
              src={vault.coverUrl}
              alt=""
            />
          )}
        </div>
        <div className={styles.vaultInfo}>
          <div className={styles.vaultInfoItem}>
            <span className="text__body__regular__overline__gray700">
              VAULT
            </span>
            <div className={styles.titleIcon}>
              <span
                className={
                  isLocked
                    ? 'text__body__semi__bold__medium__gray700'
                    : 'text__body__semi__bold__medium__gray800'
                }
              >
                {vault.title}
              </span>
              <button
                type="button"
                className={styles.button}
                data-tooltip-id={vault.id.toString()}
                onClick={() =>
                  goToPage(RouteName.VaultDetail, { id: vault.id })
                }
              >
                <NewWindowIcon />
                <Tooltip
                  id={vault.id.toString()}
                  place="bottom"
                  className={styles.tooltip}
                >
                  Open vault
                </Tooltip>
              </button>
            </div>
          </div>
          <div className={styles.typeAndOwner}>
            <div
              className={classnames(styles.vaultInfoItem, {
                [styles.hidden]: !isOpened,
              })}
            >
              <span className="text__body__regular__overline__gray700">
                VAULT TYPE
              </span>
              <span className="text__body__semi__bold__medium__gray800">
                {capitalizeFirstLetter(
                  vault.subscriptionType.replace('vault', '').replace('_', ' '),
                )}
              </span>
            </div>
            <div
              className={classnames(styles.vaultInfoItem, {
                [styles.hidden]: !isOpened,
              })}
            >
              <span className="text__body__regular__overline__gray700">
                OWNED BY
              </span>
              <span className="text__body__semi__bold__medium__gray800">
                {isOwner(user, vault) ? 'You' : vault.owner.name}
              </span>
            </div>
          </div>
        </div>
      </div>
      {isMobile && showPendingInvitations()}

      <div className={styles.rightSide}>
        <div className={styles.badgeAndChevron}>
          {isRestrictedVault(vault) && (
            <Badge
              className={classnames(
                styles.restrictedBadge,
                'text__body__semi__bold__overline__warning40',
              )}
              icon={<MinusCircleIcon />}
              message={!isMobile ? 'RESTRICTED' : undefined}
            />
          )}
          {!isMobile && showPendingInvitations()}
          {isLocked && (
            <Badge
              className={classnames(
                styles.lockedBadge,
                'text__body__semi__bold__overline__danger40',
              )}
              icon={<LockIcon />}
              message={!isMobile ? 'LOCKED' : undefined}
            />
          )}
          <ChevronIcon
            className={classnames(styles.chevronIcon, {
              [styles.vaultOpened]: isOpened,
            })}
          />
        </div>
      </div>
    </button>
  );
};

export { VaultRow };
